/**
 * «قاعة المقابلات» (owner request 2026-11-01): the day's interviews in one place, with who is waiting in
 * their room. Pressing one opens its room beside the list: the applicant's details, the audio call, and the
 * outcome (accept with a teacher account, a second interview, or decline). One whose time has come and who
 * is not in the room gets a reminder with their link.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, ChevronLeft, ChevronRight, FileText, Mic, MicOff, PhoneOff } from "lucide-react";
import { Link } from "@tanstack/react-router";
import type {
  InterviewHallItem,
  InterviewHallResponse,
  InterviewRemindResponse,
  PublicVolunteerResponse,
} from "@mubin/contracts";
import { ROLE_HEADER } from "@mubin/contracts";
import { Empty, Loading, PageTitle } from "../components/ui";
import { CreateUserModal } from "../components/user-forms";
import { InviteModal } from "../components/interview-invite";
import { api, errorText, getStoredRole, send } from "../lib/api";
import { useInterviewCall } from "../lib/interview-call";
import { ar, clock, dayKey, hijriDate, initial } from "../lib/interview";
import { useOrg } from "../lib/org";
import { timeLabel } from "../lib/volunteers";

const TZ = "Asia/Riyadh";

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

type Shown = "live" | "wait" | "late" | "soon" | "done" | "missed" | "cancelled";
function shownOf(iv: InterviewHallItem, now: number, inRoom: boolean): Shown {
  if (iv.status === "finished") return "done";
  if (iv.status === "missed") return "missed";
  if (iv.status === "cancelled") return "cancelled";
  if (inRoom && iv.waiting) return "live";
  if (iv.waiting) return "wait";
  return now >= Date.parse(iv.startsAt) ? "late" : "soon";
}

export function InterviewsPage() {
  const org = useOrg();
  const now = useNow(5000);
  const [day, setDay] = useState(() => dayKey(new Date(), TZ));
  const hall = useQuery({
    queryKey: ["interviews", org?.id, day],
    queryFn: () => api<InterviewHallResponse>(`/v1/interviews?organizationId=${org!.id}&day=${day}`),
    enabled: !!org,
    refetchInterval: 8_000,
  });
  const surahs = useQuery({
    queryKey: ["public-volunteer"],
    queryFn: () => api<PublicVolunteerResponse>("/v1/public/volunteer"),
  });
  const surahName = (id: number) => surahs.data?.surahs.find((s) => s.id === id)?.name ?? String(id);
  const [selected, setSelected] = useState<string | null>(null);
  const [joined, setJoined] = useState<string | null>(null);
  const tz = hall.data?.timezone ?? TZ;
  const list = hall.data?.interviews ?? [];
  const current = list.find((i) => i.id === selected) ?? null;
  const shift = (n: number) => {
    const d = new Date(`${day}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    setDay(d.toISOString().slice(0, 10));
    setSelected(null);
  };
  const counts = useMemo(() => {
    const c = { live: 0, wait: 0, soon: 0, done: 0, missed: 0 };
    for (const iv of list) {
      const s = shownOf(iv, now, joined === iv.id);
      if (s === "live") c.live++;
      else if (s === "wait") c.wait++;
      else if (s === "soon" || s === "late") c.soon++;
      else if (s === "done") c.done++;
      else if (s === "missed") c.missed++;
    }
    return c;
  }, [list, now, joined]);

  return (
    <>
      <PageTitle
        title="قاعة المقابلات"
        sub={`${hijriDate(`${day}T12:00:00Z`, tz)} · اضغط على المتقدم لفتح غرفته`}
        actions={
          <div className="row-actions">
            <button className="btn btn-secondary btn-sm" aria-label="اليوم السابق" onClick={() => shift(-1)}>
              <ChevronRight size={16} />
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setDay(dayKey(new Date(), TZ));
                setSelected(null);
              }}
            >
              اليوم
            </button>
            <button className="btn btn-secondary btn-sm" aria-label="اليوم التالي" onClick={() => shift(1)}>
              <ChevronLeft size={16} />
            </button>
            <Link to="/interview-slots" className="btn btn-primary btn-sm">
              ＋ أوقات متاحة
            </Link>
          </div>
        }
      />
      <div className="ivh-kpis">
        <div className="ivh-kpi live">
          <b>{ar(counts.live)}</b>
          <span>جارية الآن</span>
        </div>
        <div className="ivh-kpi">
          <b>{ar(counts.wait)}</b>
          <span>في الانتظار</span>
        </div>
        <div className="ivh-kpi">
          <b>{ar(counts.soon)}</b>
          <span>قادمة</span>
        </div>
        <div className="ivh-kpi">
          <b>{ar(counts.done)}</b>
          <span>انتهت</span>
        </div>
        <div className="ivh-kpi bad">
          <b>{ar(counts.missed)}</b>
          <span>لم يحضر</span>
        </div>
      </div>
      {hall.isPending ? (
        <Loading />
      ) : hall.isError ? (
        <Empty title="تعذر التحميل">{errorText(hall.error)}</Empty>
      ) : (
        <div className="ivh-grid">
          <div>
            {list.length === 0 ? (
              <section className="card">
                <Empty title="لا مقابلات في هذا اليوم">
                  أرسل روابط الحجز للمتقدمين من «طلبات التطوع»، وافتح الأوقات من «مواعيد المقابلات».
                </Empty>
              </section>
            ) : (
              list.map((iv) => (
                <HallItem
                  key={iv.id}
                  iv={iv}
                  tz={tz}
                  now={now}
                  shown={shownOf(iv, now, joined === iv.id)}
                  current={iv.id === selected}
                  onOpen={() => setSelected(iv.id)}
                />
              ))
            )}
          </div>
          {current ? (
            <Room
              key={current.id}
              iv={current}
              tz={tz}
              surahName={surahName}
              joined={joined === current.id}
              onJoin={() => setJoined(current.id)}
              onLeave={() => setJoined(null)}
              onChanged={() => void hall.refetch()}
            />
          ) : (
            <section className="ivh-room">
              <div className="ivh-empty-room">
                <img src="/logo-ivory.png" alt="" style={{ height: 40, opacity: 0.9 }} />
                <p style={{ marginTop: 18 }}>اختر متقدمًا من القائمة لفتح غرفته</p>
              </div>
            </section>
          )}
        </div>
      )}
    </>
  );
}

function HallItem({
  iv,
  tz,
  now,
  shown,
  current,
  onOpen,
}: {
  iv: InterviewHallItem;
  tz: string;
  now: number;
  shown: Shown;
  current: boolean;
  onOpen: () => void;
}) {
  const [time, period] = clock(iv.startsAt, tz).split(" ");
  const late = Math.max(0, Math.floor((now - Date.parse(iv.startsAt)) / 60_000));
  const soon = Math.max(0, Math.ceil((Date.parse(iv.startsAt) - now) / 60_000));
  const tag = {
    live: <span className="ivh-tag live">● جارية</span>,
    wait: (
      <span className="ivh-tag wait">
        <span className="ivh-pulse" /> في الانتظار
      </span>
    ),
    late: <span className="ivh-tag late">لم يدخل · تأخر {ar(late)} د</span>,
    soon: <span className="ivh-tag soon">{soon < 120 ? `بعد ${ar(soon)} د` : "قادمة"}</span>,
    done: <span className="ivh-tag done">انتهت{iv.applicationStatus === "accepted" ? " · مقبول" : ""}</span>,
    missed: <span className="ivh-tag late">لم يحضر</span>,
    cancelled: <span className="ivh-tag soon">أُلغيت</span>,
  }[shown];
  return (
    <button
      type="button"
      className="ivh-item"
      aria-current={current}
      onClick={onOpen}
      style={shown === "done" || shown === "cancelled" ? { opacity: 0.75 } : undefined}
    >
      <div className="ivh-time">
        <b>{time}</b>
        <span>{period === "ص" ? "صباحًا" : "مساءً"}</span>
      </div>
      <div className="ivh-av">{initial(iv.name)}</div>
      <div className="ivh-who">
        <b>{iv.name}</b>
        <div>
          حافظ {ar(iv.juz)} جزءًا{iv.hasIjazah ? " · مجاز" : ""} · {iv.nationality}
        </div>
      </div>
      {tag}
    </button>
  );
}

async function downloadCv(applicationId: string) {
  const role = getStoredRole();
  const res = await fetch(`/v1/volunteer-applications/${applicationId}/cv`, {
    credentials: "same-origin",
    headers: role ? { [ROLE_HEADER]: role } : {},
  });
  if (!res.ok) return;
  const url = URL.createObjectURL(await res.blob());
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function Room({
  iv,
  tz,
  surahName,
  joined,
  onJoin,
  onLeave,
  onChanged,
}: {
  iv: InterviewHallItem;
  tz: string;
  surahName: (id: number) => string;
  joined: boolean;
  onJoin: () => void;
  onLeave: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const now = useNow();
  const [muted, setMuted] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const call = useInterviewCall({
    enabled: joined && iv.status === "booked",
    side: "staff",
    interviewId: iv.id,
    auth: () => ({ type: "auth", interviewId: iv.id, role: getStoredRole() ?? undefined }),
    iceServers: () => api(`/v1/interviews/${iv.id}/ice-servers`),
  });
  const [since, setSince] = useState<number | null>(null);
  const talking = joined && call.peer && call.call?.phase === "connected";
  useEffect(() => {
    if (talking && since === null) setSince(Date.now());
  }, [talking, since]);
  const seconds = since ? Math.floor((now - since) / 1000) : 0;
  const decided = iv.applicationStatus === "accepted" || iv.applicationStatus === "rejected";
  const over = iv.status !== "booked";

  async function act(label: string, fn: () => Promise<void>) {
    setBusy(label);
    setMsg(null);
    try {
      await fn();
      onChanged();
      await qc.invalidateQueries({ queryKey: ["volunteers"] });
    } catch (e) {
      setMsg(errorText(e));
    } finally {
      setBusy(null);
    }
  }
  const end = (outcome: "finished" | "missed" | "cancelled") =>
    act(outcome, async () => {
      call.leave();
      onLeave();
      await send("POST", `/v1/interviews/${iv.id}/end`, { outcome });
    });
  const remind = () =>
    act("remind", async () => {
      const r = await send<InterviewRemindResponse>("POST", `/v1/interviews/${iv.id}/remind`);
      setMsg(r.ok ? "أُرسل التذكير مع رابط الدخول" : `لم يُرسل: ${r.error}`);
    });
  const decide = (status: "rejected") =>
    act(status, async () => {
      if (iv.status === "booked") await send("POST", `/v1/interviews/${iv.id}/end`, { outcome: "finished" });
      await send("POST", `/v1/volunteer-applications/${iv.applicationId}/decide`, {
        status,
        note: note.trim() || undefined,
      });
    });

  const state = !joined
    ? null
    : call.closed === "replaced"
      ? "فُتحت هذه الغرفة في نافذة أخرى"
      : call.socket !== "ready"
        ? "جارٍ الاتصال…"
        : call.call?.error === "mic_denied" || call.call?.error === "mic_unavailable"
          ? "الميكروفون غير متاح: اسمح للمتصفح باستخدامه"
          : !call.peer
            ? "المتقدم لم يدخل الغرفة بعد"
            : talking
              ? "متصل الآن"
              : call.call?.phase === "reconnecting"
                ? "انقطع الصوت، نعيد الربط…"
                : "جارٍ ربط الصوت…";

  return (
    <section className="ivh-room" aria-live="polite">
      <div className="gold" style={{ fontSize: 14 }}>
        غرفة المقابلة · {clock(iv.startsAt, tz)}
      </div>
      <div className={`iv-avatar${talking ? " live" : ""}`} aria-hidden="true">
        {initial(iv.name)}
      </div>
      <h2>{iv.name}</h2>
      <div className="gold">
        {state ??
          (iv.status === "finished"
            ? "انتهت المقابلة"
            : iv.status === "missed"
              ? "لم يحضر"
              : iv.status === "cancelled"
                ? "أُلغي الموعد"
                : iv.waiting
                  ? "في الانتظار داخل الغرفة"
                  : "لم يدخل الغرفة بعد")}
      </div>
      {talking && (
        <div className="ivh-timer" dir="ltr">
          {ar(Math.floor(seconds / 60)).padStart(2, "٠")}:{ar(seconds % 60).padStart(2, "٠")}
        </div>
      )}
      <div className="ivh-chips">
        <span>
          الحفظ: {surahName(iv.fromSurah)} ← {surahName(iv.toSurah)} ({ar(iv.juz)} جزء)
        </span>
        {iv.hasIjazah && <span>مجاز</span>}
        <span>
          الأوقات:{" "}
          {iv.times
            .map((t) =>
              t === "other" ? (iv.timesOther ?? timeLabel.other) : timeLabel[t as keyof typeof timeLabel],
            )
            .join("، ")}
        </span>
        <span className="ltr">{iv.phone}</span>
        {iv.hasCv && (
          <button type="button" onClick={() => void downloadCv(iv.applicationId)}>
            <FileText size={13} /> السيرة الذاتية
          </button>
        )}
        {iv.cvUrl && (
          <a href={iv.cvUrl} target="_blank" rel="noreferrer noopener">
            <span>
              <FileText size={13} /> رابط السيرة
            </span>
          </a>
        )}
      </div>

      {iv.status === "booked" && !joined && (
        <div className="row-actions" style={{ justifyContent: "center", marginTop: 20, gap: 10 }}>
          <button type="button" className="ivh-join" onClick={onJoin}>
            دخول الغرفة
          </button>
          {!iv.waiting && now >= Date.parse(iv.startsAt) && (
            <button
              type="button"
              className="ivh-join"
              style={{ background: "transparent", color: "#d6bd8d", border: "1.5px solid #d6bd8d" }}
              disabled={busy === "remind"}
              onClick={() => void remind()}
            >
              <BellRing size={16} /> إرسال تذكير
            </button>
          )}
        </div>
      )}
      {iv.status === "booked" && joined && (
        <div className="ivh-ctrls">
          <button
            type="button"
            className="iv-round"
            aria-pressed={muted}
            aria-label={muted ? "تشغيل الميكروفون" : "كتم الميكروفون"}
            onClick={() => {
              call.setMuted(!muted);
              setMuted(!muted);
            }}
          >
            {muted ? <MicOff size={22} /> : <Mic size={22} />}
          </button>
          {!call.peer && (
            <button
              type="button"
              className="iv-round end"
              style={{ background: "rgba(245,240,231,.12)" }}
              disabled={busy === "remind"}
              onClick={() => void remind()}
            >
              <BellRing size={18} /> تذكير
            </button>
          )}
          <button
            type="button"
            className="iv-round end"
            disabled={busy !== null}
            onClick={() => void end(call.peer || iv.waiting || since ? "finished" : "missed")}
          >
            <PhoneOff size={20} /> إنهاء المقابلة
          </button>
        </div>
      )}
      {msg && (
        <p className="small" role="status" style={{ marginTop: 12, color: "#e9dabd" }}>
          {msg}
        </p>
      )}

      {(over || joined) && !decided && iv.status !== "cancelled" && (
        <div className="ivh-result">
          <b>نتيجة المقابلة</b>
          <textarea
            aria-label="ملاحظات داخلية"
            placeholder="ملاحظات داخلية (لا تُرسل للمتقدم)…"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="ivh-result-btns">
            <button
              type="button"
              className="ivh-ok"
              disabled={busy !== null}
              onClick={() => setAccepting(true)}
            >
              قبول وإنشاء حساب
            </button>
            <button
              type="button"
              className="ivh-again"
              disabled={busy !== null}
              onClick={() =>
                void (async () => {
                  // A booked interview ends first: a new booking link is not sent while one is booked.
                  if (iv.status === "booked") await end("finished");
                  setInviting(true);
                })()
              }
            >
              مقابلة ثانية
            </button>
            <button
              type="button"
              className="ivh-no"
              disabled={busy !== null}
              onClick={() => void decide("rejected")}
            >
              اعتذار
            </button>
          </div>
        </div>
      )}
      {decided && (
        <p className="gold" style={{ marginTop: 18 }}>
          {iv.applicationStatus === "accepted" ? "✓ قُبل وأُنشئ حسابه معلمًا" : "اعتُذر عن الطلب"}
        </p>
      )}
      {iv.status === "booked" && !joined && (
        <div className="row-actions" style={{ justifyContent: "center", marginTop: 16, gap: 8 }}>
          {now >= Date.parse(iv.startsAt) + 10 * 60_000 && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ color: "#e9dabd" }}
              onClick={() => void end("missed")}
            >
              تسجيل غيابه
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ color: "#e9dabd" }}
            onClick={() => void end("cancelled")}
          >
            إلغاء الموعد
          </button>
        </div>
      )}

      <CreateUserModal
        open={accepting}
        onClose={() => setAccepting(false)}
        role="teacher"
        prefill={{ name: iv.name, phone: iv.phone, gender: iv.gender }}
        onCreated={async (userId) => {
          await act("accept", async () => {
            if (iv.status === "booked")
              await send("POST", `/v1/interviews/${iv.id}/end`, { outcome: "finished" });
            await send("POST", `/v1/volunteer-applications/${iv.applicationId}/decide`, {
              status: "accepted",
              userId,
              note: note.trim() || undefined,
            });
          });
        }}
      />
      <InviteModal
        open={inviting}
        to={[{ id: iv.applicationId, name: iv.name }]}
        onClose={() => setInviting(false)}
        onSent={() => onChanged()}
      />
    </section>
  );
}
